import * as React from "react";
import { motion, MotionProps } from "framer-motion";
import { cn } from "@/lib/utils";

export interface CTAButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof MotionProps> {
  children: React.ReactNode;
  href?: string;
  target?: string;
  rel?: string;
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, children, href, target, rel, ...props }, ref) => {
    if (href) {
      return (
        <motion.a
          href={href}
          target={target}
          rel={rel}
          className={cn(
            "relative overflow-hidden inline-flex items-center justify-center px-6 py-3 rounded-md",
            "bg-primary text-primary-foreground font-medium",
            "transition-all duration-300 cursor-pointer",
            className
          )}
          whileHover={{
            scale: 1.05,
            rotate: [0, -1, 1, 0],
            transition: {
              scale: { duration: 0.2 },
              rotate: {
                duration: 0.5,
                repeat: Infinity,
                repeatType: "reverse",
              },
            },
          }}
          whileTap={{
            scale: 0.95,
          }}
          initial={{
            boxShadow: "0 4px 15px hsl(var(--primary) / 0.4)",
          }}
          animate={{
            boxShadow: [
              "0 4px 15px hsl(var(--primary) / 0.4)",
              "0 6px 20px hsl(var(--primary) / 0.5)",
              "0 4px 15px hsl(var(--primary) / 0.4)",
            ],
          }}
          transition={{
            boxShadow: {
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            },
          }}
        >
          {/* Shine effect */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            initial={{ x: "-100%", skewX: -20 }}
            whileHover={{
              x: "100%",
              transition: {
                duration: 0.6,
                ease: "easeInOut",
              },
            }}
          />
          
          {/* Content */}
          <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
        </motion.a>
      );
    }

    return (
      <motion.button
        ref={ref}
        className={cn(
          "relative overflow-hidden inline-flex items-center justify-center px-6 py-3 rounded-md",
          "bg-primary text-primary-foreground font-medium",
          "transition-all duration-300",
          className
        )}
        whileHover={{
          scale: 1.05,
          rotate: [0, -1, 1, 0],
          transition: {
            scale: { duration: 0.2 },
            rotate: {
              duration: 0.5,
              repeat: Infinity,
              repeatType: "reverse",
            },
          },
        }}
        whileTap={{
          scale: 0.95,
        }}
        initial={{
          boxShadow: "0 4px 15px hsl(var(--primary) / 0.4)",
        }}
        animate={{
          boxShadow: [
            "0 4px 15px hsl(var(--primary) / 0.4)",
            "0 6px 20px hsl(var(--primary) / 0.5)",
            "0 4px 15px hsl(var(--primary) / 0.4)",
          ],
        }}
        transition={{
          boxShadow: {
            duration: 2,
            repeat: Infinity,
            repeatType: "reverse",
          },
        }}
        {...(props as any)}
      >
        {/* Shine effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          initial={{ x: "-100%", skewX: -20 }}
          whileHover={{
            x: "100%",
            transition: {
              duration: 0.6,
              ease: "easeInOut",
            },
          }}
        />
        
        {/* Content */}
        <span className="relative z-10 inline-flex items-center gap-2">{children}</span>
      </motion.button>
    );
  }
);

CTAButton.displayName = "CTAButton";

export { CTAButton };
